import { useFormatter, WithSkeleton } from '@capsicum/common';
import { Card, Image, SkeletonItem, Text } from '@capsicum/common-web';
import { type StaticImageData } from 'next/image';
import styled from 'styled-components';

export type WCPledgeSummaryCardProps = {
	chzAmount?: string;
	fiatAmount?: string;
	image: StaticImageData;
	imageAlt: string;
	imageHeight: number;
	imageWidth: number;
	subtitle?: string;
	testID: string;
	title: string;
};

export const WCPledgeSummaryCardComponent = ({
	chzAmount = '0',
	fiatAmount = '0',
	image,
	imageAlt,
	imageHeight,
	imageWidth,
	subtitle,
	testID,
	title
}: WCPledgeSummaryCardProps) => {
	const { formatCrypto } = useFormatter();

	return (
		<S.SummaryCard>
			<S.CardTopSection>
				<Image alt={imageAlt} height={imageHeight} src={image} width={imageWidth} />
				<Text variant="text2">{title}</Text>
				{subtitle && (
					<Text testID={`wcpledge-summary-card-subtitle-${testID}`} variant="caption2">
						{subtitle}
					</Text>
				)}
			</S.CardTopSection>
			<S.CardBottomSection>
				<Card.Title testID={`wcpledge-summary-card-chz-${testID}-amount`}>
					{formatCrypto(chzAmount, 'CHZ')}
				</Card.Title>
				<Text testID={`wcpledge-summary-card-fiat-${testID}-amount`} variant="caption1">
					{`≈${formatCrypto(fiatAmount, 'USD')}`}
				</Text>
			</S.CardBottomSection>
		</S.SummaryCard>
	);
};

export const WCPledgeSummaryCardSkeleton = () => (
	<S.SummaryCard>
		<S.CardTopSection>
			<SkeletonItem borderRadius={2} height={40} width={40} />
			<SkeletonItem borderRadius={2} height={20} width={100} />
		</S.CardTopSection>
		<S.CardBottomSection>
			<SkeletonItem borderRadius={2} height={20} width={100} />
		</S.CardBottomSection>
	</S.SummaryCard>
);

export const WCPledgeSummaryCard = WithSkeleton(WCPledgeSummaryCardComponent, WCPledgeSummaryCardSkeleton);

export namespace S {
	export const SummaryCard = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
		padding: ${({ theme }) => theme.spacings.small};
		justify-content: space-between;
		width: 100%;
	`;

	export const CardTopSection = styled.div`
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const CardBottomSection = styled.div`
		align-self: flex-end;
		text-align: right;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
